import { apiFetch } from '../admin/jscontrol/apiClient';
import { computeRatingDistribution } from './productReviewHelpers';

const storefrontOptions = { useStorefrontAuth: true };

const ratingSummaryCache = new Map();
const ratingSummaryInflight = new Map();

export function invalidateProductRatingSummary(productId) {
  const id = Number(productId);
  if (!Number.isFinite(id) || id <= 0) return;
  ratingSummaryCache.delete(id);
  ratingSummaryInflight.delete(id);
}

export async function fetchProductRatingSummary(productId) {
  const id = Number(productId);
  if (!Number.isFinite(id) || id <= 0) return { rating: 0, count: 0, distribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 } };

  if (ratingSummaryCache.has(id)) {
    return ratingSummaryCache.get(id);
  }

  if (ratingSummaryInflight.has(id)) {
    return ratingSummaryInflight.get(id);
  }

  const request = fetchProductReviewsForProduct(id)
    .then(({ items }) => {
      const summary = computeRatingDistribution(items);
      ratingSummaryCache.set(id, summary);
      ratingSummaryInflight.delete(id);
      return summary;
    })
    .catch(() => {
      const empty = { rating: 0, count: 0, distribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 } };
      ratingSummaryCache.set(id, empty);
      ratingSummaryInflight.delete(id);
      return empty;
    });

  ratingSummaryInflight.set(id, request);
  return request;
}

export async function fetchProductReviews(params = {}) {
  const qs = new URLSearchParams({
    pageIndex: String(params.pageIndex ?? 1),
    pageSize: String(params.pageSize ?? 10),
    search: params.search ?? '',
    sortColumn: params.sortColumn ?? 'ProductReviewId',
    sortBy: (params.sortBy ?? 'DESC').toUpperCase(),
  });
  if (params.productId) qs.set('productId', String(params.productId));
  if (params.userId) qs.set('userId', String(params.userId));

  const authOptions = params.useStorefront ? storefrontOptions : undefined;
  const { data } = await apiFetch(`/ProductReview/GetAll?${qs}`, authOptions);
  return {
    items: Array.isArray(data?.data) ? data.data : [],
    totalRecords: data?.totalRecords ?? 0,
  };
}

export async function fetchProductReviewsForProduct(productId, pageSize = 50) {
  return fetchProductReviews({ productId, pageIndex: 1, pageSize, useStorefront: true });
}

export async function fetchProductReviewById(productReviewId) {
  const { data } = await apiFetch(`/ProductReview/GetById/${productReviewId}`);
  if (data?.success === false) throw new Error(data?.message || 'Review not found.');
  return data?.data ?? data;
}

export async function createProductReviewApi(payload) {
  const productId = Number(payload?.ProductId ?? payload?.productId);
  const { data } = await apiFetch('/ProductReview/Create', {
    method: 'POST',
    useStorefrontAuth: true,
    data: payload,
  });
  if (Number.isFinite(productId) && productId > 0) {
    invalidateProductRatingSummary(productId);
  }
  if (data?.success === false) throw new Error(data?.message || 'Could not create review.');
  return data?.data?.ProductReviewId ?? data?.data?.productReviewId ?? null;
}

export async function updateProductReviewApi(payload) {
  const { data } = await apiFetch('/ProductReview/Update', { method: 'PUT', data: payload });
  if (data?.success === false) throw new Error(data?.message || 'Could not update review.');
}

export async function deleteProductReviewApi(productReviewId) {
  const { data } = await apiFetch(`/ProductReview/Delete/${productReviewId}`, { method: 'DELETE' });
  if (data?.success === false) throw new Error(data?.message || 'Could not delete review.');
}
